import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import getCookie from '../../../utils/getCookie';
import axios from 'axios';
import { useRouter, NextRouter } from 'next/router';
import Layout from '../../../layout/Layout';
import Link from 'next/link';
import SidebarComponent from '../../../components/leftNavBar/LeftSidebar';
import CollectionsComponent from '../../../components/products/Collections';
import { ProvideParams } from '../../../modules/NextProviders';
import { ProductsLayoutProps } from '../../../types/pages/products/CollectionsTypes';
import { CollectionResponse } from '../../../types/api/CollectionsIndexTypes';
import { CategoryResponse } from '../../../types/api/CategoriesIndexTypes';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { ENV } from '../../../constants/environments';
import { seo, seoCollections } from '../../../staticData/Seo';
import { useSession } from 'next-auth/react';
import NoSearchResults from '../../../components/products/NoSearchResults';
import { RightCaretIcon } from '../../../modules/Icons';
import FreeCollectionsSwitch from '../../../components/products/FreeCollectionsSwitch';
import { ApplicationState } from '../../../types/state/storeTypes';
import { CategoryCollectionsSearchParams } from '../../../types/api/CategoriesIndexTypes';
import cache from '../../../helpers/redis';
import Filters from '../../../components/products/Filters';
import errorRedirectHandler from '../../../helpers/errorRedirectHandler';
import {
  isolateSelectedCategoryOrSubcategorySlug,
  findAndFormatCategoryBySlug,
} from '../../../helpers/pages/collections/category/categoriesHelpers';
import CategoryPageVisitPlugin from '../../../components/user-interest-analytics/CategoryPageVisitPlugin';
import ProductGridSkeleton from '../../../components/shared/ProductGridSkeleton';
import ProductGridSkeletonTrigger from '../../../components/shared/ProductGridSkeletonTrigger';

import { QUERY_PARAMS as ALGOLIA_QUERY_PARAMS } from '../../../constants/algolia';
import { LIBRARY_IDS } from '../../../constants/api';
import { PRODUCT_FILTER_KEYS } from '../../../constants/ui';
import { BROWSER_FINGERPRINT_COOKIE_NAME } from '../../../constants/utils';

const {
  TEXT_SEARCH_V2: TEXT_SEARCH_KEY_V2,
  TEXT_SEARCH_V1: TEXT_SEARCH_KEY_V1,
  ASSET_TYPE: ASSET_TYPE_KEY,
} = PRODUCT_FILTER_KEYS;
const { LIBRARY_ID_3D } = LIBRARY_IDS;

const apiUrl: string = `${ENV.api.baseURL}`;
const collectionUrl = `${apiUrl}/collections`;

const defaultCollectionParams = {
  library_id: 3,
  desc: false
};

const fetchCollections = async (
  session: any,
  collectionsUrl: string,
  collectionParams: CategoryCollectionsSearchParams,
  router: NextRouter,
) => {
  const browser_fingerprint = getCookie(BROWSER_FINGERPRINT_COOKIE_NAME);
  let params = {
    ...collectionParams,
    user_token: browser_fingerprint,
  };
  if (ENV.useV2ProductEndpoints === 'true') {
    collectionsUrl = collectionUrl.replace('v1', 'v2');
    if (router.query.sort_by) (params as any).sort_by = router.query.sort_by as string;
  };
  const collectionsRes = await axios.get(collectionsUrl, {
    params,
    headers: {
      authorization: session?.user?.token
    }
  });
  if (collectionsRes.data.query_id) {
    const newQuery = { ...router.query, [ALGOLIA_QUERY_PARAMS.QUERY_ID]: collectionsRes.data.query_id };
    router.replace(
      {
        pathname: router.pathname,
        query: newQuery,
      },
      undefined,
      { shallow: true },
    );
  }
  return collectionsRes?.data;
};

const CategoryPage3D: React.FC<ProductsLayoutProps> = (props) => {
  const { categories } = props;

  const router = useRouter();
  const session = useSession();
  const categoryFilterName = ENV.useV2ProductEndpoints ? 'category' : 'category_permalink';

  const [collectionParams, setCollectionParams] = useState<CategoryCollectionsSearchParams>({
    slug: router.query.slug as string,
    ...router.query,
    ...defaultCollectionParams
  });
  const [collections, setCollections] = useState<Array<CollectionResponse>>();
  const [totalPages, setTotalPages] = useState<number>();
  const [currentPage, setCurrentPage] = useState<number>();
  const [loadingCollections, setLoadingCollections] = useState<boolean>(false);
  const [category, setCategory] = useState<CategoryResponse>();
  const [categoryInfo, setCategoryInfo] = useState<any>({});
  const [headContent, setHeadContent] = useState<any>({});
  const [searchResultsCount, setSearchResultsCount] = useState<number>();
  const [displaySkeleton, setDisplaySkeleton] = useState(false);

  const getTextSearchKey = (): string => {
    return ENV.useV2ProductEndpoints ? TEXT_SEARCH_KEY_V2 : TEXT_SEARCH_KEY_V1;
  }

  const loadCollections = async () => {
    const slug: string = isolateSelectedCategoryOrSubcategorySlug(router.query.slug as string);
    const searchTerm = router.query[getTextSearchKey()];
    const assetTypeValue = router.query[ASSET_TYPE_KEY];
    const collectionParams = {
      library_id: LIBRARY_ID_3D,
      desc: false,
      [categoryFilterName]: slug,
    };
    if (searchTerm)
      collectionParams[getTextSearchKey()] = searchTerm as string;
    if (assetTypeValue)
      collectionParams[ASSET_TYPE_KEY] = assetTypeValue as string;
    setCollectionParams(collectionParams);
    setLoadingCollections(true);
    const result = await fetchCollections(session.data, collectionUrl, collectionParams, router);
    setSearchResultsCount(result.total_count);
    setCategoryCollectionsState(result.data, result.total_pages, result.page);
    setLoadingCollections(false);
  };

  const setCategoryCollectionsState = (
    collections: Array<CollectionResponse>,
    totalPages: number,
    currentPage: number
  ) => {
    setCollections(collections);
    setTotalPages(totalPages);
    setCurrentPage(currentPage);
  };

  const clearCategoryCollectionsState = () => {
    setCollections(undefined);
    setTotalPages(undefined);
    setCurrentPage(undefined);
  };

  const updateCategory = () => {
    const category: CategoryResponse = findAndFormatCategoryBySlug(
      router.query.slug as string,
      categories
    );
    setCategory(category);
    const categoryInfo = {
      name: category ? category.name + ' VFX Stock Footage' : 'All Collections',
      headerImage: category ? category.header_image : '/images/collections-default.jpg',
      keywords: category ? category.keywords.concat(seo.keywords) : seo.keywords
    };
    setCategoryInfo(categoryInfo);
    setHeadContent({
      title: categoryInfo.name,
      description: dynamicSeo?.description || seo.description,
      keywords: categoryInfo.keywords,
      og: {
        title: categoryInfo.name,
        image: categoryInfo.headerImage
      },
      canonical: router.asPath.split('?')[0].replace('/', ''),
    });
  };

  const onNextPageOfCollections = (result: {
    collections: Array<CollectionResponse>;
    pageNum: number;
  }) => {
    const slug: string = isolateSelectedCategoryOrSubcategorySlug(router.query.slug as string);
    const _collectionParams = {
      page: result.pageNum,
      slug,
      [categoryFilterName]: slug,
      ...defaultCollectionParams
    };
    setCollectionParams(_collectionParams);
  };

  useEffect(() => {
    if (!router.query.slug) return;
    clearCategoryCollectionsState();
    updateCategory();
    loadCollections();
  }, [router.query.slug, router.query[ASSET_TYPE_KEY], router.query[getTextSearchKey()]]);

  const dynamicSeo = seoCollections.find((item) => item.path == router.asPath);

  return (
    <Layout head={headContent}>
      <ProvideParams>
        {({ id, slug, query }) => (
          <div className="products-container">
            <div id="products-page" className="flex flex-col md:flex-row w-full bg-[#252A2D]">
              <SidebarComponent
                id={id}
                slug={slug || 'all'}
                categories={categories.sort((a, b) => a.name.localeCompare(b.name))}
                category={category}
                params={collectionParams}
                shallowLinks={true}
              />
              <div className="w-full p-6">
                <section
                  className={`${category?.slug != 'free-vfx' ? 'h-[260px]' : 'h-[398px]'
                    } relative w-full flex items-center rounded-[5px] mb-5 bg-cover bg-center bg-no-repeat`}
                  style={
                    category?.slug != 'free-assets'
                      ? { backgroundImage: `linear-gradient( rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6) ), url(${categoryInfo.headerImage})` }
                      : { backgroundImage: `linear-gradient( rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6) ), url(/img/freevfx.png)` }
                  }>
                  {category?.slug == 'free-vfx' && <FreeCollectionsSwitch />}
                  <div className="w-11/12 mx-auto text-center">
                    {category?.slug != 'free-vfx' && (
                      <>
                        <h1 className="uppercase font-bold text-[24px] md:text-[36px] lg:text-[48px] leading-none">
                          {category?.parent && (
                            <span className="stroke-white-50 text-white-50">
                              <Link href={`/collections/${category?.parent.slug}/category`}>
                                {category?.parent.name}
                              </Link>
                              &nbsp;
                              <RightCaretIcon className="inline relative top-[-5px]" />
                              &nbsp;
                            </span>
                          )}
                          {category?.name}
                        </h1>
                        <p className="text-gray-500 text-[16px] md:text-[22px] mt-4">
                          {category?.total_elements > 0 && (
                            <span>{category?.total_elements} Assets | </span>
                          )}
                          {category?.total_collections} Collections
                        </p>
                      </>
                    )}
                    {category?.slug == 'free-vfx' && (
                      <>
                        <h1 className="uppercase font-bold text-[24px] md:text-[36px] lg:text-[48px] leading-none">
                          {category?.total_elements} Free VFX assets
                        </h1>
                        <p className="text-gray-500 text-[16px] md:text-[22px] mt-6">
                          Download High-Quality 2K VFX Stock Footage completely free!
                        </p>
                      </>
                    )}
                  </div>
                </section>

                <Filters
                  libraryId={LIBRARY_ID_3D}
                  category={category}
                />

                {collectionParams[getTextSearchKey()]?.length > 0 && searchResultsCount == 0 && (
                  <NoSearchResults term={collectionParams[getTextSearchKey()]} />
                )}
                {!!collections && !displaySkeleton && (
                  <CollectionsComponent
                    id={id}
                    slug={
                      isolateSelectedCategoryOrSubcategorySlug(router.query.slug as string) || 'all'
                    }
                    query={query}
                    name={categoryInfo.name}
                    collections={collections}
                    totalPages={totalPages}
                    currentPage={currentPage}
                    collectionParams={collectionParams}
                    onNextPageLoaded={(data: {
                      collections: Array<CollectionResponse>;
                      pageNum: number;
                    }) => onNextPageOfCollections(data)}
                  />
                )}
                <ProductGridSkeleton visible={displaySkeleton || loadingCollections} />
                <ProductGridSkeletonTrigger
                  setDisplaySkeleton={setDisplaySkeleton}
                />
              </div>
            </div>
          </div>
        )}
      </ProvideParams>
      <CategoryPageVisitPlugin category={category} />
    </Layout>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx: GetServerSidePropsContext) => {
  if (ctx.params.slug == 'free-for-subscribers') {
    return {
      redirect: {
        permanent: false,
        destination: '/collections/free-for-subscribers'
      }
    };
  }

  const categoriesUrl: string = `${apiUrl}/categories?limit=50&library_id=3`;
  let categories: any;
  try {
    categories = await cache('avfx_3d_categories_all', 3600, async () => {
      const categoriesRes = await axios.get(categoriesUrl);
      return categoriesRes?.data?.data;
    });
  } catch (error) {
    return errorRedirectHandler(error);
  }

  if (!ctx.params.slug?.length) {
    return {
      redirect: {
        permanent: false,
        destination: '/3d'
      }
    };
  }

  const parentSlug = (ctx.params.slug as string).split('--')[0];
  const childSlug = (ctx.params.slug as string).split('--')[1];
  const categoryFilterName = process.env.USE_V2_PRODUCT_ENDPOINTS ? 'category' : 'category_permalink';

  let query = ctx.query;
  if (ctx.params.slug?.length > 0) {
    query = { ...query, [categoryFilterName]: childSlug || parentSlug };
  }

  return {
    props: {
      categories: categories.map(c => ({ ...c, library_id: 3 }))
    }
  };
};

const mapStateToProps = (state: ApplicationState) => ({
  categoriesCache: state.categoriesCache
});

export default connect(mapStateToProps)(CategoryPage3D);
